<template>
    <div class="web-feedback-panel shadow show">
        <div class="panel-header">
            <h3 class="panel-header-title">Eerder gemaakte melding</h3>
            <!-- Close icon -->
            <icon-close
                :id="'web-feedback-information-close'"
                @close="$_onCancel()"
                class="d-inline-block float-right"
            >
            </icon-close>
        </div>
        <div
            class="panel-content"
            :class="{ 'has-pagination': showPagination }"
        >
            <div>
                <!-- Pagination -->
                <Pagination
                    v-if="showPagination"
                    :features="objects"
                    :indexStart="index"
                    @indexChanged="$_onIndexChanged"
                >
                </Pagination>
            </div>
            <information-display
                :fields="webThemeFeedbackFields"
                :objectData="objects[this.index]"
            />
            <!-- Me Also Button -->
            <div class="form-group sticky-bottom fixed-bottom">
                <b-button
                    v-if="showJoinFeedbackButton"
                    @click.stop="$_onReportSame()"
                    id="btn-report-also"
                    class="mr-1"
                >
                    {{ $t('reportFeedbackJoin') }}
                </b-button>
                <b-button
                    v-if="showDifferentFeedbackButton"
                    @click="$_onReportNew()"
                    id="btn-new-feedback"
                >
                    {{ $t('reportFeedbackDifferent') }}
                </b-button>
                <b-button
                    v-if="showDeleteFeedbackButton"
                    @click="$_onDeleteFeedback()"
                    id="btn-delete-feedback"
                    class="ml-1"
                >
                    {{ $t('btnDeleteLabel') }}
                </b-button>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState, mapGetters } from 'vuex';
    import { FIELD_TYPE, NEDGLOBE_VIEWER } from '../../../../constants';
    import { objectData } from '../../../../services/selections';
    import modalMixin from '../../../../components/Mixins/ModalMixin.vue';
    import InformationDisplay from '../../webFeedbacks/InformationDisplay.vue';
    import IconClose from '../../../IconClose.vue';
    import Pagination from '../../../Pagination.vue';

    export default {
        components: {
            'icon-close': IconClose,
            Pagination,
            'information-display': InformationDisplay
        },

        props: { webfeedbackInformation: { type: Object, required: true } },

        data() {
            return {
                // Constants
                fieldTypeConst: FIELD_TYPE,

                index: 0,
                // I want also report this
                joinExistingFeedback: false
            };
        },

        mixins: [modalMixin],

        computed: {
            ...mapState(['webTheme']),
            ...mapGetters(['getWebThemeFeedback', 'getLocale']),

            showPagination() {
                return this.objects.length > 1;
            },
            affiliatedFeedbackCount() {
                return this.objects.length > 0
                    ? this.objects[this.index].affiliatedFeedbackCount
                    : 0;
            },
            showJoinFeedbackButton() {
                return this.webTheme.data.showJoinFeedbackButton;
            },
            showDifferentFeedbackButton() {
                return this.webTheme.data.showDifferentFeedbackButton;
            },
            showDeleteFeedbackButton() {
                return this.webTheme.data.showDeleteFeedback;
            },
            webThemeFeedbackFields() {
                return this.getWebThemeFeedback?.data?.feedbackFields;
            },
            featuresInLayers: function () {
                if (!this.webfeedbackInformation?.results) {
                    return null;
                }
                return this.webfeedbackInformation.results.map(
                    ({ features, layer }) => ({ features, layer })
                );
            },
            features: function () {
                if (!this.featuresInLayers) {
                    return null;
                }
                return this.featuresInLayers.flatMap(({ features }) => features);
            },
            objects: function () {
                if (!this.featuresInLayers) {
                    return null;
                }
                const allObjects = this.featuresInLayers
                    .map(({ features, layer }) =>
                        objectData(
                            features,
                            layer,
                            false,
                            false,
                            this.getLocale
                        ).map((item) => ({ ...item, layer }))
                    )
                    .map((item) =>
                        item.map(({ info, layer, feature }) => ({
                            ...info,
                            layer,
                            feature
                        }))
                    )
                    .flatMap((info) => info);
                return allObjects
                    .filter(({ aansluitnummer }) => aansluitnummer?.value === null)
                    .map((item) => ({
                        ...item,
                        affiliatedFeedbackCount: allObjects.filter((_item) => {
                            return (
                                _item.aansluitnummer?.value ===
                                item.meldingsnummer.value
                            );
                        }).length
                    }));
            },
            location() {
                if (!this.webfeedbackInformation?.location?.position) {
                    return null;
                }
                return this.webfeedbackInformation.location.position;
            }
        },

        methods: {
            $_onIndexChanged(index) {
                this.index = index;
            },

            /**
             * Close
             */
            $_onCancel() {
                // Close this panel
                this.$_hidePanel();
            },

            /**
             * Get domain description on the basis of field.value
             * @param  {object} field the field object
             * @return {string} domain description
             */
            $_getDomainDescription(field) {
                let domainValue = '';

                if (field.domain) {
                    const foundDomain = field.domain.keyvalues.find((kv) => {
                        return kv.key === field.value;
                    });
                    if (foundDomain) {
                        domainValue = foundDomain.value;
                    }
                }
                return domainValue;
            },

            $_onReportSame() {
                const originalFeedback = this.objects[this.index].feature;
                const position = originalFeedback.getGeometry().getCoordinates();
                this.$emit('next', {
                    type: 'reportAlso',
                    data: this.objects[this.index],
                    position,
                    geometryType: originalFeedback.getGeometry().getType()
                });
            },
            $_onReportNew() {
                const originalFeedback = this.objects[this.index].feature;
                const position = originalFeedback.getGeometry().getCoordinates();
                this.$emit('next', {
                    type: 'reportNew',
                    data: this.objects[this.index],
                    position,
                    geometryType: originalFeedback.getGeometry().getType()
                });
            },

            async $_onDeleteFeedback() {
                const confirm = await this.$store.dispatch('askConfirm', {
                    message: 'deleteWebFeedbackConfirmationMessage',
                    options: {
                        cancelTitle: this.$t('btnTitleNo'),
                        okTitle: this.$t('btnTitleYes')
                    }
                });
                if (confirm) {
                    try {
                        const feature = this.objects[this.index].feature;
                        const encodedFeatureId = Buffer.from(
                            feature.getId()
                        ).toString('base64');

                        // No authentication is required for web
                        await this.$store.dispatch('useApiData', {
                            name: 'DeleteWebFeedbacks',
                            data: {
                                feedbackFeatureIds: [encodedFeatureId],
                                webThemeId: this.$store.getters.getWebTheme.id,
                                deletedBy: this.$store.getters.getUserInfo.sessionId
                            }
                        });

                        // Refresh feedback & labels
                        this.$store.dispatch('refreshWebFeedback', {
                            mapName: null
                        });
                        this.$_onCancel();
                    } catch (error) {
                        this.showToast({
                            message: error.message,
                            variant: 'danger',
                            title: this.$t('deleteFailureTitle'),
                            autoHideDelay: 10000
                        });
                    }
                }
            },

            /**
             * Hide the web feedback panel
             */
            $_hidePanel() {
                this.$store.commit('RESET_MAP_FEATURES', {
                    mapName: null,
                    customLayerName:
                        NEDGLOBE_VIEWER.customLayers.printable.selection
                });
                this.$store.commit('RESET_MAP_FEATURES', {
                    mapName: null,
                    customLayerName: NEDGLOBE_VIEWER.customLayers.printable.selected
                });
                this.$emit('close');
            }
        },

        watch: {
            /**
             * Start a feedback interaction form on feedbackData changes
             * @param  {[type]} newValue [description]
             * @param  {[type]} oldValue [description]
             * @return {[type]}          [description]
             */
            webfeedbackInformation: {
                handler: function (newValue) {
                    this.index = 0;
                    const layer = this.objects[0]?.layer;
                    const customLayerName =
                        NEDGLOBE_VIEWER.customLayers.printable.selection;
                    this.$store.commit('RESET_MAP_FEATURES', {
                        mapName: null,
                        customLayerName
                    });
                    const { computedStyles } = layer.extended;
                    const features = this.objects.map(({ feature }) => feature);
                    this.$store.commit('SHOW_MAP_FEATURE', {
                        mapName: null,
                        customLayerName,
                        features,
                        computedStyles,
                        styleType: NEDGLOBE_VIEWER.styleDisplayTypes.selection
                    });
                },
                immediate: true
            },
            index: {
                handler: function (newVal) {
                    const layer = this.objects[0]?.layer;
                    const { computedStyles } = layer.extended;
                    const customLayerName =
                        NEDGLOBE_VIEWER.customLayers.printable.selected;

                    this.$store.commit('SHOW_MAP_FEATURE', {
                        mapName: 'main',
                        customLayerName,
                        feature: this.objects[newVal]?.feature,
                        computedStyles,
                        styleType: NEDGLOBE_VIEWER.styleDisplayTypes.selected,
                        reset: true
                    });
                }
            }
        }
    };
</script>

<style scoped lang="scss">
.web-feedback-panel {
    @include feedback-panel;
}

label {
    font-weight: normal;
}

.web-feedback-subtitle {
    padding-top: 20px;
}
.web-feedback-subtitle:first-of-type {
    padding-top: 0px;
}

.web-feedback-affiliated {
    padding: 12px 0px;
}

.readonly-scrollable {
    max-height: 104px;
    overflow-y: auto;
}

@media (max-width: 767px) {
    .web-feedback-panel.show {
        width: 100%;
    }
}
</style>
